import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidDisplayName } from '@os/shared';
import { useApi } from '../../app/providers';
import { ApiError } from '../../lib/api';
import type { InvitationPeek, InviteScope } from '../../lib/sharing-types';
import { Button, Field, Input } from '../../ui';
import { FormError } from '../auth/AuthLayout';
import { newPasswordSchema } from '../auth/schemas';
import { useCaptcha } from '../auth/Turnstile';

type Errors = { name?: string; password?: string; confirm?: string; form?: string };

/**
 * « Créer votre compte » (P17): the account of the invited address, with the password the person chooses. The Worker
 * creates it (admin API, after checking the invitation); then this form signs in and accepts, exactly once. Once the
 * account exists the form never asks the Worker again: a failure after that goes on at the login page.
 */
export function InviteSignup({
  token,
  invite,
  onAccepted,
  onRefused,
  onSignIn,
}: {
  token: string;
  invite: InvitationPeek;
  onAccepted: (scope: InviteScope) => void;
  /** The database or the Worker refuses the invitation itself (invalid, expired, mismatch). */
  onRefused: (err: unknown) => void;
  /** The account exists: the person continues by signing in (account_exists, or a sign-in that failed). */
  onSignIn: () => void;
}) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const captcha = useCaptcha();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  const role = t(`sharing.roles.${invite.role}`, { defaultValue: invite.role });
  const intro =
    invite.scopeType === 'org'
      ? t('invite.introOrg', { org: invite.orgName, role })
      : t('invite.introScope', { name: invite.scopeName, org: invite.orgName, role });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    // The Worker's rules (@os/shared account.ts): a name of 1 to 60 characters, no control character.
    const displayName = name.trim();
    const nameOk = isValidDisplayName(displayName);
    const parsedPassword = newPasswordSchema.safeParse(password);
    const next: Errors = {
      ...(!nameOk && { name: t('invite.nameRequired') }),
      ...(!parsedPassword.success && { password: t('auth.passwordTooShort') }),
      ...(parsedPassword.success && password !== confirm && { confirm: t('settings.passwordMismatch') }),
    };
    setErrors(next);
    if (!nameOk || !parsedPassword.success || next.confirm) {
      (next.name ? nameRef : next.password ? passwordRef : confirmRef).current?.focus();
      return;
    }
    setBusy(true);
    let email: string;
    try {
      ({ email } = await api.signupWithInvitation(token, { password, displayName, locale: i18n.language.startsWith('fr') ? 'fr' : 'en' }));
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      setBusy(false);
      if (code === 'account_exists') return setExists(true);
      if (code === 'invitation_invalid' || code === 'invitation_expired') return onRefused(err);
      setErrors({ form: t(code === 'invalid' ? 'invite.refused' : code === 'limit' ? 'auth.tooManyAttempts' : 'auth.genericError') });
      return;
    }
    // The account exists from here on. Signing in replaces a guest's anonymous session; accepting happens here, once:
    // the page's own effect has already started and does not accept again when the session flips.
    try {
      await api.auth.signInWithPassword(email, password, captcha.token);
    } catch {
      return onSignIn();
    }
    try {
      onAccepted(await api.acceptInvitation(token));
    } catch (err) {
      onRefused(err);
    }
  };

  if (exists) {
    return (
      <div className="flex flex-col gap-4">
        <p role="alert" className="font-sans text-fg">
          {t('invite.accountExists')}
        </p>
        <Button variant="primary" className="self-start" onClick={onSignIn}>
          {t('guest.signIn')}
        </Button>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
      <p className="font-sans text-sm text-fg-muted">{intro}</p>
      <Field label={t('auth.email')} hint={t('invite.emailHint')}>
        <Input type="email" autoComplete="username" value={invite.email} readOnly />
      </Field>
      <Field label={t('settings.displayName')} error={errors.name}>
        <Input ref={nameRef} autoComplete="name" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t('auth.password')} error={errors.password} hint={t('invite.passwordHint')}>
        <Input ref={passwordRef} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
      <Field label={t('settings.confirmPassword')} error={errors.confirm}>
        <Input ref={confirmRef} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </Field>
      {captcha.widget}
      <FormError message={errors.form ?? null} />
      <Button variant="primary" type="submit" loading={busy} disabled={!captcha.ready}>
        {busy ? t('invite.creating') : t('invite.submit')}
      </Button>
    </form>
  );
}
